import { useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError, formatBRT, nextSlots } from "@/lib/api";

export const TOPIC_LABELS = { divulgacao: "Divulgação do servidor", vips: "VIPs", eventos: "Eventos", promocoes: "Promoções" };
export const SERVER_LABELS = { ambos: "Os dois servidores", chernarus: "BRASIL+ Chernarus", nasdara: "BRASIL+ Nasdara" };

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <p className="dz-mono text-xs uppercase tracking-widest text-zinc-400">{label}</p>
    {children}
  </div>
);

const Pick = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="rounded-none bg-black/40 border-white/15 text-white" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      {Object.entries(options).map(([k, v]) => (
        <SelectItem key={k} value={k} data-testid={`${testId}-option-${k}`}>{v}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export const GenerateDialog = ({ open, onOpenChange, onCreated }) => {
  const slots = nextSlots();
  const [topic, setTopic] = useState("divulgacao");
  const [server, setServer] = useState("ambos");
  const [slot, setSlot] = useState("next");
  const [instructions, setInstructions] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/posts/generate", { topic, server, instructions, scheduled_for: slot === "next" ? null : slot });
      toast.success("A IA está criando o post. Ele aparece abaixo em instantes.");
      setInstructions("");
      onOpenChange(false);
      onCreated();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121314] border-white/10 text-white rounded-none max-w-lg" data-testid="generate-dialog">
        <DialogHeader>
          <DialogTitle className="dz-heading text-2xl uppercase font-black">Gerar post com IA</DialogTitle>
          <DialogDescription className="text-zinc-400">O texto e a imagem são criados pela IA. Você revisa antes de aprovar.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Tema"><Pick value={topic} onChange={setTopic} options={TOPIC_LABELS} testId="generate-topic-select" /></Field>
          <Field label="Servidor"><Pick value={server} onChange={setServer} options={SERVER_LABELS} testId="generate-server-select" /></Field>
          <Field label="Horário">
            <Pick value={slot} onChange={setSlot} testId="generate-slot-select"
              options={{ next: "Definir ao aprovar (próximo horário)", ...Object.fromEntries(slots.map((s) => [s, formatBRT(s)])) }} />
          </Field>
          <Field label="Instruções (opcional)">
            <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} maxLength={1500} rows={4}
              placeholder="Ex: evento de PVP no sábado às 21h, prêmio de 50 mil em moedas"
              className="rounded-none bg-black/40 border-white/15 text-white" data-testid="generate-instructions-input" />
          </Field>
          <button onClick={submit} disabled={busy} data-testid="generate-submit-button"
            className="dz-play w-full flex items-center justify-center gap-2 py-3 bg-[#c81e1e] text-white dz-heading text-lg font-extrabold uppercase tracking-wider disabled:opacity-60">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />} Gerar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
